import React, { useState, useEffect } from "react";
import { Modal } from "antd";
import Buttons from "../../Components/Buttons/Buttons";
import FormInputs from "../../Components/FormInputs/FormInputs";
import Loader from "../../Components/Loader/Loader";
import { SETUP_ROLL_NUM_PREFIX, ROLL_NUM_PREFIX_CONFIGURATION } from "../../Utlis/apiPath";
import { getApi, postApi } from "../../Utlis/apiService";
import { errorToast, successToast } from "../../Services/ToastHelper";

const RollNumberConfigModal = ({ isVisible, onClose }) => {
  const [configData, setConfigData] = useState({
    prefix: "",
    sequenceStart: 1,
  });
  const [configErrors, setConfigErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [currentConfig, setCurrentConfig] = useState(null);

  useEffect(() => {
    if (isVisible) {
      fetchCurrentConfig();
    }
  }, [isVisible]);

  const fetchCurrentConfig = async () => {
    setIsLoading(true);
    try {
      const { status, data } = await getApi(ROLL_NUM_PREFIX_CONFIGURATION);
      console.log("API Response:", data); // Debug log
      
      if (status === 200 && data) {
        // Handle array response - get the first active configuration
        const configArray = Array.isArray(data) ? data : [data];
        const activeConfig = configArray.find(config => config.isActive) || configArray[0];
        
        console.log("Active Config:", activeConfig); // Debug log
        
        if (activeConfig) {
          setCurrentConfig(activeConfig);
          setConfigData({
            prefix: activeConfig.prefix || "",
            sequenceStart: activeConfig.sequenceStart || 1,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching roll number config:", error);
      // If no config exists, use default values
      setConfigData({
        prefix: "",
        sequenceStart: 1,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const validateConfigData = () => {
    let objErrors = {};
    if (!configData.prefix || configData.prefix.trim() === "") {
      objErrors.prefix = "Prefix is required";
    }
    if (!configData.sequenceStart || configData.sequenceStart < 1) {
      objErrors.sequenceStart = "Sequence start must be at least 1";
    }
    return objErrors;
  };

  const handleSaveConfig = async () => {
    const errors = validateConfigData();
    if (Object.keys(errors).length > 0) {
      setConfigErrors(errors);
      return;
    }

    try {
      setIsLoading(true);
      const payload = {
        prefix: configData.prefix,
        sequenceStart: parseInt(configData.sequenceStart),
      };
      const response = await postApi(SETUP_ROLL_NUM_PREFIX, payload);

      if (response.status === 200) {
        successToast(response.message || "Roll number configuration saved successfully");
        handleClose();
      } else {
        errorToast(response.message || "Failed to save configuration");
      }
    } catch (error) {
      console.error("Error saving roll number config:", error);
      errorToast(
        error?.response?.data?.message ||
          error?.message ||
          "An error occurred while saving configuration"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setConfigData({
      prefix: "",
      sequenceStart: 1,
    });
    setConfigErrors({});
    setCurrentConfig(null);
    onClose();
  };

  return (
    <Modal
      open={isVisible}
      onCancel={handleClose}
      footer={null}
      width={500}
      title="Roll Number Configuration"
    >
      {isLoading && (
        <div className="loading-overlay">
          <Loader />
        </div>
      )}
      
      {currentConfig && (
        <div
          style={{
            backgroundColor: "#f0f2f5",
            padding: "15px",
            borderRadius: "8px",
            marginBottom: "20px",
          }}
        >
          <h3 style={{ marginBottom: "10px" }}>Current Configuration</h3>
          <p style={{ margin: "5px 0" }}>
            <strong>Prefix:</strong> {currentConfig.prefix}
          </p>
          <p style={{ margin: "5px 0" }}>
            <strong>Sequence Start:</strong> {currentConfig.sequenceStart}
          </p>
          <p style={{ margin: "5px 0" }}>
            <strong>Current Sequence:</strong> {currentConfig.currentSequence}
          </p>
          <p style={{ margin: "5px 0", fontSize: "12px", color: "#666" }}>
            Next Roll Number: {currentConfig.prefix}{String(currentConfig.currentSequence + 1).padStart(3, '0')}
          </p>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        <FormInputs
          title="Prefix"
          type="text"
          placeholder="e.g., FY23-24"
          value={configData.prefix || ""}
          onChange={(e) => {
            setConfigData({
              ...configData,
              prefix: e.target.value,
            });
            setConfigErrors({ ...configErrors, prefix: "" });
          }}
          errorMessage={configErrors.prefix}
        />
        <FormInputs
          title="Sequence Start"
          type="number"
          placeholder="Enter starting sequence number"
          value={configData.sequenceStart || ""}
          onChange={(e) => {
            const value = parseInt(e.target.value) || "";
            setConfigData({
              ...configData,
              sequenceStart: value,
            });
            setConfigErrors({ ...configErrors, sequenceStart: "" });
          }}
          errorMessage={configErrors.sequenceStart}
        />
        
        <div
          style={{
            backgroundColor: "#e6f7ff",
            padding: "12px",
            borderRadius: "6px",
            border: "1px solid #91d5ff",
          }}
        >
          <p style={{ margin: 0, fontSize: "14px" }}>
            <strong>Preview:</strong> {configData.prefix || "[Prefix]"}{String(configData.sequenceStart || 1).padStart(3, '0')}
          </p>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
        <Buttons
          variant="secondary"
          onClick={handleClose}
        >
          Cancel
        </Buttons>
        <Buttons
          variant="primary"
          onClick={handleSaveConfig}
        >
          Save Configuration
        </Buttons>
      </div>
    </Modal>
  );
};

export default RollNumberConfigModal;